import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PiCaretDown, PiCheck, PiMagnifyingGlass } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import {
  getMenuItemClasses,
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
} from 'src/modules/shared/styles/dropdown.styles';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import {
  baseTriggerInputClasses,
  controlTriggerLabelLineHeightClasses,
  controlTriggerPaddingClasses,
  controlTriggerSizeClasses,
  getDisabledClasses,
  getInputAppearanceClasses,
} from 'src/modules/shared/styles/component.styles';
import { nodeToString } from 'src/modules/shared/utils/formatting/node.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';

interface SelectOption {
  /** Plain text for search and selected summaries when content is rich JSX. */
  textValue?: string;
  content: React.ReactNode | string;
  value: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  disabled?: boolean;
  loading?: boolean;
  containerClassName?: string;
  placeholder?: string;
}

const indicatorSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'h-3 w-3',
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-4.5 w-4.5',
  lg: 'h-5 w-5',
};

const indicatorIconClasses: Record<ComponentSize, string> = {
  '2xs': 'h-2 w-2',
  xs: 'h-2.5 w-2.5',
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

const headerButtonSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'text-[10px]',
  xs: 'text-xs',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

const filterOptions = (options: SelectOption[], query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) =>
    (option.textValue ?? nodeToString(option.content))
      .toLowerCase()
      .includes(normalizedQuery)
  );
};

const resolveTriggerLabel = (
  options: SelectOption[],
  value: string[],
  placeholder: string,
  selectedSummaryLabel: (count: number) => string
) => {
  if (value.length === 0) {
    return placeholder;
  }

  const selectedLabels = value
    .map((selectedValue) => {
      const option = options.find((item) => item.value === selectedValue);
      return option?.textValue ?? nodeToString(option?.content);
    })
    .filter(Boolean);

  if (selectedLabels.length === 1) {
    return selectedLabels[0];
  }

  if (selectedLabels.length === 2) {
    return selectedLabels.join(', ');
  }

  return selectedSummaryLabel(selectedLabels.length);
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  size = 'md',
  variant = 'primary',
  appearance = 'outline',
  disabled = false,
  loading = false,
  containerClassName = '',
  placeholder,
}) => {
  const { t } = useI18n();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  const resolvedPlaceholder =
    placeholder ?? t('shared.multiSelect.placeholder');

  const filteredOptions = useMemo(
    () => filterOptions(options, query),
    [options, query]
  );

  const triggerLabel = useMemo(
    () =>
      resolveTriggerLabel(options, value, resolvedPlaceholder, (count) =>
        t('shared.multiSelect.selectedSummary', { count: String(count) })
      ),
    [options, resolvedPlaceholder, t, value]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
  }, [open]);

  useEffect(() => {
    const measure = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
      }
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [open, options, size, variant, value]);

  const toggleValue = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((currentValue) => currentValue !== optionValue));
      return;
    }

    onChange([...value, optionValue]);
  };

  const enabledOptionValues = options
    .filter((option) => !option.disabled)
    .map((option) => option.value);

  const hasAllEnabledSelected =
    enabledOptionValues.length > 0 &&
    enabledOptionValues.every((optionValue) => value.includes(optionValue));

  const triggerClasses = [
    ...baseTriggerInputClasses,
    'text-left',
    'flex items-center gap-2',
    getInputAppearanceClasses(variant, appearance),
    controlTriggerSizeClasses[size],
    controlTriggerPaddingClasses[size],
    'pr-2',
    getDisabledClasses(disabled || loading),
  ].join(' ');

  return (
    <div className={`relative w-full ${containerClassName}`}>
      <Popover
        handler={
          <button
            type="button"
            ref={triggerRef}
            disabled={disabled || loading}
            className={triggerClasses}
          >
            <span
              className={`min-w-0 flex-1 truncate text-left ${controlTriggerLabelLineHeightClasses[size]}`}
            >
              {triggerLabel}
            </span>
            <PiCaretDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        placement="bottom-start"
        className="w-full"
        contentClassName="z-[10000] !max-w-none p-0"
        contentStyle={
          triggerWidth == null
            ? undefined
            : {
                width: triggerWidth,
                minWidth: triggerWidth,
                maxWidth: triggerWidth,
              }
        }
        disabled={disabled}
        isLoading={loading}
      >
        <div
          className="flex flex-col bg-white"
          style={
            triggerWidth == null
              ? undefined
              : {
                  width: triggerWidth,
                  minWidth: triggerWidth,
                  maxWidth: triggerWidth,
                }
          }
        >
          <div className="border-b border-neutral-100 p-2">
            <TextInput
              ref={searchInputRef}
              size={size}
              variant={variant}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('shared.multiSelect.searchPlaceholder')}
              className="w-full"
            />
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-2">
            <button
              type="button"
              className={`font-medium text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 ${headerButtonSizeClasses[size]}`}
              onClick={() => onChange(enabledOptionValues)}
              disabled={
                enabledOptionValues.length === 0 || hasAllEnabledSelected
              }
            >
              {t('shared.multiSelect.selectAll')}
            </button>
            <button
              type="button"
              className={`font-medium text-secondary-500 disabled:cursor-not-allowed disabled:opacity-50 ${headerButtonSizeClasses[size]}`}
              onClick={() => onChange([])}
              disabled={value.length === 0}
            >
              {t('shared.multiSelect.clear')}
            </button>
          </div>

          <div className="flex max-h-72 flex-col gap-1 overflow-auto p-1">
            {filteredOptions.length === 0 ? (
              <EmptyState
                title={t('shared.multiSelect.emptyLabel')}
                description={t('shared.multiSelect.emptyDescription')}
                icon={<PiMagnifyingGlass className="h-4 w-4" />}
                size="xs"
                className="py-6"
              />
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                const rowClasses = getMenuItemClasses(
                  size,
                  isSelected,
                  false,
                  option.disabled
                );

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={isSelected}
                    disabled={option.disabled}
                    className={rowClasses}
                    onClick={() => toggleValue(option.value)}
                  >
                    <span
                      className={`flex flex-shrink-0 items-center justify-center rounded border transition-colors ${
                        indicatorSizeClasses[size]
                      } ${
                        isSelected
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-neutral-300 bg-white text-transparent'
                      }`}
                    >
                      <PiCheck className={indicatorIconClasses[size]} />
                    </span>
                    {typeof option.content === 'string' ? (
                      <Text
                        size={getControlTextSize(size)}
                        weight={getMenuItemContentWeight(isSelected)}
                        color={getMenuItemContentColor(isSelected)}
                        className={`${getMenuItemContentClasses()} min-w-0 flex-1 truncate`}
                      >
                        {option.content}
                      </Text>
                    ) : (
                      <div className="min-w-0 flex-1">{option.content}</div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default MultiSelect;
